/**
 * ч.1120 — підтвердження розпроведення закриття місяця.
 *
 * Розпроведення прибирає все, що створив документ: проводки закриття і
 * документи кроків (амортизація, зарплата, переоцінка, податки ФОП). Перед
 * дією показуємо перелік — що зникне і що залишиться (нарахування зарплати,
 * за яким уже проведено виплати). Дані — з того самого плану, за яким сервер
 * виконує розпроведення.
 */
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

import { monthCloseRuns, type UnpostItem } from '@/api/month_close_runs';

export function UnpostItems({ items }: { items: UnpostItem[] }) {
  return (
    <ul className="mc__warn-list">
      {items.map((it, i) => (
        <li key={i}>
          {it.label}
          {it.document_number ? ` № ${it.document_number}` : ''}
          {it.detail ? ` — ${it.detail}` : ''}
        </li>
      ))}
    </ul>
  );
}

export default function UnpostDialog({ runId, mode, pending, onConfirm, onClose }: {
  runId: string;
  /** unpost — лише розпровести; repost — розпровести і провести знову. */
  mode: 'unpost' | 'repost';
  pending: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const q = useQuery({
    queryKey: ['month-close-unpost-preview', runId],
    queryFn: () => monthCloseRuns.unpostPreview(runId),
    gcTime: 0,
  });
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const plan = q.data;
  const title = mode === 'repost' ? 'Перепровести закриття місяця' : 'Розпровести закриття місяця';
  return (
    <div role="dialog" aria-modal="true" aria-label={title} onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'var(--overlay)',
        display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="card" onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(680px, 94vw)', maxHeight: '84vh', overflowY: 'auto', margin: 0 }}>
        <h3 style={{ marginTop: 0 }}>{title}</h3>
        <p style={{ marginTop: 0 }}>
          {mode === 'repost'
            ? 'Усе, що створило закриття, буде прибрано, і місяць одразу закриється заново — вже за поточними документами.'
            : 'Усе, що створило закриття, буде прибрано. Документ лишиться непроведеним — його можна провести знову.'}
        </p>
        {q.isLoading && <p style={{ color: 'var(--muted)' }}>Завантаження…</p>}
        {q.isError && <div className="error">Не вдалося отримати перелік — спробуйте ще раз</div>}
        {plan && (
          <>
            <div className="mc__warn-title">Буде прибрано</div>
            {plan.removed.length > 0
              ? <UnpostItems items={plan.removed} />
              : <div style={{ color: 'var(--muted)' }}>нічого — закриття не створило ні проводок, ні документів</div>}
            {plan.kept.length > 0 && (
              <div className="mc__panel mc__panel--info" style={{ marginTop: '0.6rem' }}>
                <div className="mc__panel-head">Залишиться</div>
                <UnpostItems items={plan.kept} />
              </div>
            )}
            {plan.warnings.map((w, i) => (
              <div key={i} className="mc__panel mc__panel--info" style={{ marginTop: '0.6rem' }}>{w}</div>
            ))}
          </>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
          <button type="button" onClick={onClose}>скасувати</button>
          <button type="button" className="danger" disabled={!plan || pending} onClick={onConfirm}>
            {pending ? 'виконується…' : mode === 'repost' ? 'перепровести' : 'розпровести'}
          </button>
        </div>
      </div>
    </div>
  );
}
